// Quick add a buyer — the high-volume entry path, for a specialist typing while
// somebody is on the phone. Name, email, phone, State, Source and the two intake
// pickers (assignee, advertisement), and nothing else: everything on
// `CreateBuyerLeadPayload` that is not here is editable afterwards on the full
// form, and a create surface that asks for all of it is one nobody uses mid-call.
//
// One optional question rides along: "Who is buying?". Left blank it is an
// individual and the request carries no key for it; only a company type reveals
// two more rows (company name and title, both required for it). The website is
// left to the full form.
//
// The duplicate hook and the warning banner below are shared with
// `PasteToCreateBuyerModal` and are exported from here, co-located with the
// first consumer.

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { Button, FormField, Input, useToast } from "@/design-system/components/ui";
import { extractErrorMessage } from "@/api/client";
import {
  buyersApi,
  type BuyerLeadDetailDto,
  type BuyerLeadDuplicateDto,
  type BuyerLeadSource,
  type CreateBuyerLeadPayload,
} from "@/api/buyers";
import {
  AdvertisementPicker,
  useAdvertisementAttribution,
} from "@/components/buyers/AdvertisementPicker";
import { BuyerTypeCreateFields } from "@/components/buyers/BuyerTypeCreateFields";
import {
  EMPTY_BUYER_TYPE_CREATE,
  buyerTypeCreatePayload,
  companyDetailsProblem,
  type BuyerTypeCreateState,
} from "@/components/buyers/buyerTypeCreate";
import { SpecialistCandidateSelect } from "@/components/buyers/SpecialistQuickPick";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import { emailFieldError } from "@/lib/emailValidator";
import { phoneFieldError } from "@/lib/phoneValidator";
import { US_STATES } from "@/lib/usStates";
import { BUYER_SOURCES, buyerLabel } from "./buyerFilters";

interface QuickAddBuyerModalProps {
  onClose: () => void;
  /**
   * 🔴 <b>The id of the buyer this created.</b> It used to be called bare, so
   * the one thing the write produces — the pointer to the new record — was
   * thrown away on the line after it arrived, and the host could do nothing but
   * invalidate. `PresentBuyerWizard` and `ConvertToBorrowerModal` both hand
   * theirs over; this is the same contract.
   *
   * <p>Fires on EVERY create, including each Save &amp; next — the host owns the
   * invalidation for both modals, so a path that skipped it would leave the list
   * and its stat cards describing a set the specialist has already added to.</p>
   */
  onCreated: (buyerId: string) => void;
}

/**
 * Suspected duplicates for the contact details being typed.
 *
 * <p>🔴 <b>Detect, warn, never block.</b> Nothing this returns gates a save —
 * the caller renders {@link BuyerDuplicateWarning} and the specialist decides.
 * A failed probe resolves to no matches rather than to an error, because an
 * advisory warning that breaks the form is worse than no warning.</p>
 *
 * <p>Debounced so the probe fires once the specialist stops typing rather than
 * per keystroke, and registered under the shared key factory so creating a
 * buyer re-evaluates it.</p>
 */
export function useBuyerDuplicateCheck(
  email: string,
  phone: string,
  excludeId?: string,
): BuyerLeadDuplicateDto[] {
  const [debounced, setDebounced] = useState({ email: "", phone: "" });

  useEffect(() => {
    const t = window.setTimeout(
      () => setDebounced({ email: email.trim(), phone: phone.trim() }),
      400,
    );
    return () => window.clearTimeout(t);
  }, [email, phone]);

  const probe = useQuery({
    queryKey: buyerKeys.duplicateCheck(debounced.email, debounced.phone, excludeId),
    queryFn: () =>
      buyersApi.duplicateCheck({
        email: debounced.email || undefined,
        phone: debounced.phone || undefined,
        excludeId,
      }),
    enabled: !!debounced.email || !!debounced.phone,
    retry: false,
  });

  return probe.data?.matches ?? [];
}

/** The advisory notice. Each match opens in a NEW TAB so the half-typed form
 *  behind it is not lost. */
export function BuyerDuplicateWarning({ matches }: { matches: BuyerLeadDuplicateDto[] }) {
  if (matches.length === 0) return null;
  return (
    <div className="rounded-md border border-status-warning bg-white-warm px-space-3 py-space-2" role="status">
      <p className="text-small font-semibold text-onyx m-0">
        Possible duplicate{matches.length > 1 ? "s" : ""} — you can still save:
      </p>
      <ul className="list-none p-0 m-0 mt-space-1 space-y-space-1">
        {matches.map((m) => (
          <li key={m.id} className="flex flex-wrap items-center gap-space-2 text-caption text-onyx">
            <span className="font-semibold">
              {m.displayName.trim() || m.email || m.phone || "Unnamed buyer"}
            </span>
            <span className="text-gray-mid">matched on {m.matchedOn}</span>
            <span className="text-gray-mid">· {buyerLabel(m.status)}</span>
            <span className="text-gray-mid">
              · {m.assignedSpecialistName ?? "Unassigned"}
            </span>
            <a
              href={`/buyers/${m.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-gold-shadow hover:underline"
            >
              Open existing
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * What to tell the specialist about an assignment made at creation, read off
 * the CREATE RESPONSE — never re-derived client-side, because the server is
 * what decided the row's team.
 *
 * <p>`teamSetByRouting` (Q4 B) means the named assignee had no team, so the
 * row's team came from the ordinary router instead of from them; the notice
 * says so rather than naming a team the assignee is not on. Null when nobody
 * was assigned — the host's own "Buyer created." is the whole story then.</p>
 */
export function assignmentNotice(
  created: Pick<BuyerLeadDetailDto, "assignedSpecialistName" | "buyerTeamName" | "teamSetByRouting">,
): string | null {
  const name = created.assignedSpecialistName?.trim();
  if (!name) return null;
  if (created.teamSetByRouting) return `Assigned to ${name} · team set by routing`;
  const team = created.buyerTeamName?.trim();
  return team ? `Assigned to ${name} · on ${team}` : `Assigned to ${name}`;
}

export function QuickAddBuyerModal({ onClose, onCreated }: QuickAddBuyerModalProps) {
  const toast = useToast();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  // Per buyer, NOT kept across Save & next: the next caller is a different
  // person, who may well be buying personally.
  const [buyerType, setBuyerType] = useState<BuyerTypeCreateState>(EMPTY_BUYER_TYPE_CREATE);
  const [showCompanyErrors, setShowCompanyErrors] = useState(false);
  const [state, setState] = useState("");
  const [source, setSource] = useState<BuyerLeadSource>("Unknown");
  // Both kept across Save & next, like State and Source: a batch typed off one
  // campaign's call sheet is attributed and placed once, not per buyer.
  const attribution = useAdvertisementAttribution(setSource);
  // Null = the queue (the default). The server writes the same grant-ledger
  // row for an assignee named here that the queue's own assign writes.
  const [assignTo, setAssignTo] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const matches = useBuyerDuplicateCheck(email, phone);

  // Optional, but the server rejects a malformed one — so catch it here rather
  // than letting a 400 land after the specialist has moved on.
  const emailError = emailFieldError(email, { required: false });
  const phoneError = phoneFieldError(phone, { required: false });
  const hasName = !!firstName.trim() || !!lastName.trim();
  const canSave = hasName && !emailError && !phoneError;

  const save = async (saveAndNext: boolean) => {
    setError(null);
    if (!hasName) {
      setError("Enter at least a first or last name.");
      return;
    }
    if (emailError || phoneError) {
      setError(emailError ?? phoneError ?? null);
      return;
    }
    // The server refuses the same thing in the same words.
    const companyProblem = companyDetailsProblem(
      buyerType.buyerType,
      buyerType.companyName,
      buyerType.title,
    );
    if (companyProblem) {
      setShowCompanyErrors(true);
      setError(companyProblem);
      return;
    }
    setSaving(true);
    try {
      const payload: CreateBuyerLeadPayload = {
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        state: state || null,
        source,
        // Blank type adds no key at all; see `buyerTypeCreatePayload`.
        ...buyerTypeCreatePayload(buyerType),
        // Omitted rather than sent null: no pick is no attribution, and the
        // picker never renders for a caller without BuyerAdvertising.Read.
        ...(attribution.advertisementId ? { advertisementId: attribution.advertisementId } : {}),
        // Likewise: omitted lands the buyer unassigned in the queue.
        ...(assignTo ? { assignToSpecialistUserId: assignTo } : {}),

        // Records that a human SAW the duplicate warning and continued. The
        // rule is detect, warn, never block, so this acknowledgement is the
        // only thing separating a considered decision from an unnoticed
        // collision. True only when matches were actually on screen.
        confirmCreateDespiteDuplicate: matches.length > 0,
      };
      const created = await buyersApi.create(payload);
      const notice = assignmentNotice(created);
      if (notice) toast.info(notice);
      onCreated(created.id);
      if (saveAndNext) {
        setFirstName("");
        setLastName("");
        setEmail("");
        setPhone("");
        setBuyerType(EMPTY_BUYER_TYPE_CREATE);
        setShowCompanyErrors(false);
        setMessage("Saved — enter the next buyer.");
        window.setTimeout(() => setMessage(null), 2000);
      } else {
        onClose();
      }
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Quick add buyer"
      description="Unassigned buyers land in the queue. Everything else is editable afterwards."
      size="md"
      footer={
        <>
          <Button variant="text" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={saving || !canSave}
            onClick={() => void save(true)}
          >
            <Save size={14} strokeWidth={1.5} aria-hidden /> Save &amp; next
          </Button>
          <Button size="sm" disabled={saving || !canSave} onClick={() => void save(false)}>
            {saving ? "Saving…" : matches.length > 0 ? "Save anyway" : "Save"}
          </Button>
        </>
      }
    >
      <div className="grid gap-space-3">
        <BuyerDuplicateWarning matches={matches} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
          <FormField label="First name">
            <Input value={firstName} autoFocus onChange={(e) => setFirstName(e.target.value)} />
          </FormField>
          <FormField label="Last name">
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </FormField>
          <FormField label="Email" error={emailError}>
            <Input
              type="email"
              value={email}
              invalid={!!emailError}
              onChange={(e) => setEmail(e.target.value)}
            />
          </FormField>
          <FormField label="Phone" error={phoneError}>
            <Input
              type="tel"
              value={phone}
              invalid={!!phoneError}
              onChange={(e) => setPhone(e.target.value)}
            />
          </FormField>
          <BuyerTypeCreateFields
            value={buyerType}
            onChange={setBuyerType}
            showWebsite={false}
            showErrors={showCompanyErrors}
          />
          <FormField label="State">
            <Select value={state} onChange={(e) => setState(e.target.value)}>
              <option value="">—</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name} ({s.code})
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="Source">
            <Select
              value={source}
              onChange={(e) => attribution.setSourceByHand(e.target.value as BuyerLeadSource)}
            >
              {BUYER_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {buyerLabel(s)}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField
            label="Assign to"
            helperText="Leave unassigned and the buyer lands in the queue."
          >
            <SpecialistCandidateSelect buyerLeadId={null} value={assignTo} onChange={setAssignTo} />
          </FormField>
          <AdvertisementPicker
            value={attribution.advertisementId}
            onChange={attribution.pickAdvertisement}
          />
        </div>

        {error && (
          <p className="text-caption text-status-error m-0" role="alert">
            {error}
          </p>
        )}
        {message && <p className="text-caption text-status-success m-0">{message}</p>}
      </div>
    </Modal>
  );
}
